import { httpClient } from '@/shared/api';
import type {
  RiskTriageIndicator,
  RiskTriageReportView,
  RiskTriageReviewRow,
} from './riskTriageReport.dto';

const PREFIX = '/app/school-classification';

function readNum(o: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function readStr(o: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (v == null) continue;
    if (typeof v === 'string') {
      const t = v.trim();
      if (t) return t;
      continue;
    }
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    if (typeof v === 'boolean') return v ? 'true' : 'false';
    /* Skip objects/arrays here — use dedicated readers (e.g. readPeerGroupDisplay). */
  }
  return '';
}

/** ABP / proxies sometimes wrap the DTO in `result` or `data`. */
function unwrapPayload(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if ('result' in o && o.result != null && typeof o.result === 'object') {
    return o.result as Record<string, unknown>;
  }
  if ('data' in o && o.data != null && typeof o.data === 'object') {
    return o.data as Record<string, unknown>;
  }
  return o;
}

/**
 * Peer group may be a flat string or a nested DTO. Avoid treating objects as `String(v)` (would show wrong text).
 */
function readPeerGroupDisplay(o: Record<string, unknown>): string {
  const flatKeys = [
    'peerGroupLabel',
    'PeerGroupLabel',
    'peerGroupName',
    'PeerGroupName',
    'peerGroupTitle',
    'PeerGroupTitle',
    'peerGroupDescription',
    'PeerGroupDescription',
    'peerGroupText',
    'PeerGroupText',
    'peerCategory',
    'PeerCategory',
    'peerCategoryLabel',
    'PeerCategoryLabel',
    'groupLabel',
    'GroupLabel',
    'peer_group',
    'peer_group_label',
    'peerGroup',
    'PeerGroup',
  ];
  for (const k of flatKeys) {
    const v = o[k];
    if (v == null) continue;
    if (typeof v === 'object') continue;
    if (typeof v === 'string') {
      const t = v.trim();
      if (t) return t;
      continue;
    }
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }

  const nestedKeys = [
    'peerGroup',
    'PeerGroup',
    'peerGroupInfo',
    'PeerGroupInfo',
    'peerGroupCategory',
    'PeerGroupCategory',
    'peerGroupDto',
    'PeerGroupDto',
    'peerCategory',
    'PeerCategory',
  ];
  for (const k of nestedKeys) {
    const v = o[k];
    if (!v || typeof v !== 'object' || Array.isArray(v)) continue;
    const rec = v as Record<string, unknown>;
    const inner = readStr(rec, [
      'label',
      'Label',
      'name',
      'Name',
      'title',
      'Title',
      'description',
      'Description',
      'displayName',
      'DisplayName',
      'peerGroupLabel',
      'PeerGroupLabel',
    ]);
    if (inner) return inner;
  }

  return '—';
}

function readBool(o: Record<string, unknown>, keys: string[]): boolean {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'boolean') return v;
    if (v === 1 || v === '1' || v === 'true' || v === 'True') return true;
  }
  return false;
}

/** If value looks like a fraction in (-1,1), treat as ratio for variance display. */
function normalizeVariancePercent(raw: number | undefined): number {
  if (raw === undefined || !Number.isFinite(raw)) return 0;
  if (Math.abs(raw) <= 1 && raw !== 0) return raw * 100;
  return raw;
}

function mapIndicator(raw: unknown, index: number): RiskTriageIndicator {
  if (!raw || typeof raw !== 'object') {
    return {
      name: `Indicator ${index + 1}`,
      schoolValue: 0,
      peerMedian: 0,
      variancePercentage: 0,
      isFlagged: false,
    };
  }
  const o = raw as Record<string, unknown>;
  const name =
    readStr(o, [
      'label',
      'Label',
      'name',
      'Name',
      'indicatorName',
      'IndicatorName',
      'title',
      'Title',
      'code',
      'Code',
    ]) || `Indicator ${index + 1}`;
  const schoolValue = readNum(o, ['schoolValue', 'SchoolValue', 'value', 'Value']) ?? 0;
  const peerMedian = readNum(o, ['peerMedian', 'PeerMedian', 'peerAverage', 'PeerAverage']) ?? 0;
  const vRaw = readNum(o, ['variancePercentage', 'VariancePercentage', 'variance', 'Variance']);
  return {
    name,
    schoolValue,
    peerMedian,
    variancePercentage: normalizeVariancePercent(vRaw),
    isFlagged: readBool(o, ['isFlagged', 'IsFlagged', 'flagged', 'Flagged']),
  };
}

function mapReviewRow(raw: unknown, index: number): RiskTriageReviewRow {
  if (!raw || typeof raw !== 'object') {
    return {
      id: `row-${index}`,
      schoolName: '—',
      region: '—',
      peerGroup: '—',
      anomalyScore: 0,
      primaryAnomaly: '—',
      status: '—',
      indicators: [],
    };
  }
  const o = raw as Record<string, unknown>;
  const schoolId = readStr(o, ['schoolId', 'SchoolId', 'id', 'Id']);
  const schoolName = readStr(o, ['schoolName', 'SchoolName', 'name', 'Name']) || '—';
  const region =
    readStr(o, ['region', 'Region', 'governorateName', 'GovernorateName', 'areaName', 'AreaName']) || '—';
  const peerGroup = readPeerGroupDisplay(o);
  const anomalyScore = readNum(o, ['anomalyScore', 'AnomalyScore', 'score', 'Score']) ?? 0;
  const primaryAnomaly =
    readStr(o, ['primaryAnomaly', 'PrimaryAnomaly', 'primaryFlag', 'PrimaryFlag', 'anomaly', 'Anomaly']) || '—';
  const status = readStr(o, ['status', 'Status', 'reviewStatus', 'ReviewStatus']) || '—';
  const indRaw = o.indicators ?? o.Indicators;
  const indicators: RiskTriageIndicator[] = Array.isArray(indRaw)
    ? indRaw.map((item, i) => mapIndicator(item, i))
    : [];

  const id =
    readStr(o, ['rowId', 'RowId']) ||
    (schoolId && /^[0-9a-f-]{36}$/i.test(schoolId) ? schoolId : '') ||
    `${schoolName}-${index}`;

  return {
    id,
    schoolId: schoolId || undefined,
    schoolName,
    region,
    peerGroup,
    anomalyScore,
    primaryAnomaly,
    status,
    indicators,
  };
}

function mapRiskTriageReport(raw: unknown): RiskTriageReportView {
  const root = unwrapPayload(raw);
  if (!root) {
    return {
      totalFlaggedSchools: 0,
      highSeverityCount: 0,
      averageDataCompleteness: 0,
      globalValidationStatus: '—',
      reviewQueue: [],
    };
  }
  const o = root;
  const queueRaw = o.reviewQueue ?? o.ReviewQueue;
  const queue: RiskTriageReviewRow[] = Array.isArray(queueRaw)
    ? queueRaw.map((row, i) => mapReviewRow(row, i))
    : [];

  let avgComplete = readNum(o, ['averageDataCompleteness', 'AverageDataCompleteness']) ?? 0;
  if (avgComplete > 0 && avgComplete <= 1) avgComplete *= 100;

  return {
    totalFlaggedSchools: Math.round(readNum(o, ['totalFlaggedSchools', 'TotalFlaggedSchools']) ?? 0),
    highSeverityCount: Math.round(readNum(o, ['highSeverityCount', 'HighSeverityCount']) ?? 0),
    averageDataCompleteness: Math.min(100, Math.max(0, avgComplete)),
    globalValidationStatus:
      readStr(o, [
        'globalValidationStatus',
        'GlobalValidationStatus',
        'validationStatus',
        'ValidationStatus',
      ]) || '—',
    reviewQueue: queue,
  };
}

export const riskTriageReportApi = {
  getReport: async (year: number): Promise<RiskTriageReportView> => {
    const raw = await httpClient.get<unknown>(
      `${PREFIX}/risk-triage-report?year=${encodeURIComponent(String(year))}`
    );
    return mapRiskTriageReport(raw);
  },
};
